<template>
  <v-container>
    <v-row justify="center">
      <v-col cols="12" sm="10" md="8">
        <v-container
          v-if="projects && projects.length === 0"
          class="d-flex justify-center mb-12"
        >
          <span class="body-1">
            {{ $t("project_list.no_projects") }}
          </span>
        </v-container>

        <v-row
          class="d-flex justify-end mb-10"
          :class="
            projects && projects.length > 0 ? 'justify-end' : 'justify-center'
          "
        >
          <niko-button
            icon-before="$iconAdd"
            :text="$t('project_list.add')"
            @onClick="isAddProjectModalVisible = true"
            primary
          ></niko-button>
        </v-row>

        <v-list
          outlined
          :color="$vuetify.theme.dark ? '#1A1A1a' : 'grey lighten-2'"
          class="project-list"
        >
          <v-list-item-group
            outlined
            style="max-height: 500px"
            class="overflow-y-auto"
          >
            <template v-for="(project, index) in projects">
              <div :key="project.project_id">
                <v-divider v-if="index === 0"></v-divider>

                <v-list-item class="project-list-item" outlined two-line>
                  <v-list-item-content @click="openProject(project)">
                    <v-list-item-title>{{ project.name }}</v-list-item-title>
                    <v-list-item-subtitle>{{
                      project.buildings.length > 1
                        ? $t("project_list.buildings", [
                            project.buildings.length,
                          ])
                        : project.buildings.length === 1
                        ? project.buildings[0].address
                        : ""
                    }}</v-list-item-subtitle>
                  </v-list-item-content>

                  <v-list-item-action>
                    <v-container class="d-flex row pa-0 ma-0 align-center">
                      <h4
                        v-if="project.state"
                        style="width: 140px"
                        class="mr-4"
                      >
                        {{ projectState(project.state) }}
                      </h4>
                      <niko-container-dropdown
                        :items="getDropdownItems(project.state)"
                        @clicked="executeSelectedAction($event)"
                        :project="project"
                        width="205px"
                        height="160px"
                      />
                    </v-container>
                  </v-list-item-action>
                </v-list-item>

                <v-divider></v-divider>
              </div>
            </template>
          </v-list-item-group>
        </v-list>
      </v-col>
    </v-row>
    <niko-modal
      :isVisible="isAddProjectModalVisible"
      :inputs="addProjectModalInputs"
      :title="$t('project_list.modal.add.title')"
      :primaryButtonText="$t('project_list.modal.buttons.add')"
      :secondaryButtonText="$t('project_list.modal.buttons.cancel')"
      :info="$t('project.modal.standard_info')"
      @onSubmit="addNew"
      @onClose="isAddProjectModalVisible = false"
    />
    <niko-modal
      v-if="selectedProject"
      :isVisible="isDeletingModalVisible"
      :title="$t('project_list.modal.delete.title')"
      :warning="$t('project_list.modal.delete.warning')"
      :inputs="[]"
      :primaryButtonText="$t('project_list.modal.buttons.delete')"
      :secondaryButtonText="$t('project_list.modal.buttons.cancel')"
      @onSubmit="onDelete"
      @onClose="
        isDeletingModalVisible = false;
        selectedProject = null;
      "
    />
    <niko-modal
      :isVisible="isCompanyInfoModalVisible"
      :inputs="companyInfoModalInputs"
      :title="$t('project_list.modal.company_info.title')"
      :info="$t('project_list.modal.company_info.edit_info')"
      :extraInfo="
        !activeSubscription
          ? $t('project_list.modal.company_info.freemium_info')
          : ''
      "
      @onClose="isCompanyInfoModalVisible = false"
    />
    <niko-modal-wrapper
      v-if="selectedProject"
      :showModal="isRollbackModalVisible"
      :title="$t(`project_list.modal.rollback.${selectedProject.state}.title`)"
      :primaryButtonText="
        $t(`project_list.modal.rollback.${selectedProject.state}.button`)
      "
      :secondaryButtonText="$t('project_list.modal.buttons.cancel')"
      @onSubmit="onRollback"
      @onClose="
        isRollbackModalVisible = false;
        selectedProject = null;
      "
    >
      <p class="mt-4 mb-8">
        {{
          this.$t(`project_list.modal.rollback.${selectedProject.state}.text`)
        }}
      </p>
    </niko-modal-wrapper>
    <share
      v-if="selectedProject"
      :isVisible="isShareModalVisible"
      :projectName="selectedProject.name"
      @onClose="isShareModalVisible = false"
    />
    <niko-modal-wrapper
      :showModal="isGeneralUpgradeModalVisible"
      :title="$t('premium.modal.title')"
      :primaryButtonText="$t('premium.modal.primary_button')"
      @onClose="isGeneralUpgradeModalVisible = false"
      @onSubmit="openFastSpringPopup"
    >
      <p class="body1 mt-9 mb-7">
        {{ $t("premium.modal.general_text") }}
      </p>
    </niko-modal-wrapper>
  </v-container>
</template>
<script>
import { mapActions, mapMutations, mapState } from "vuex";
import NikoButton from "./UI/NikoButton";
import NikoModal from "./UI/NikoModal";
import NikoModalWrapper from "./UI/NikoModalWrapper";
import NikoContainerDropdown from "./UI/NikoContainerDropdown";
import Share from "@/components/share/Share";
import { getProjectStatePresentationName } from "@/utils/projectStateHelpers";
import { eventBus } from "@/components/EventBus";

export default {
  name: "list-of-projects",
  components: {
    NikoContainerDropdown,
    NikoButton,
    NikoModal,
    NikoModalWrapper,
    Share,
  },
  data: function () {
    return {
      selectedProject: null,
      isAddProjectModalVisible: false,
      isDeletingModalVisible: false,
      isShareModalVisible: false,
      isGeneralUpgradeModalVisible: false,
      isRollbackModalVisible: false,
      isCompanyInfoModalVisible: false,
    };
  },
  created() {
    eventBus.$on("openCompanyInfoModal", () => {
      this.isCompanyInfoModalVisible = true;
    });
  },
  destroyed() {
    eventBus.$off("openCompanyInfoModal", () => {
      this.isCompanyInfoModalVisible = false;
    });
  },
  computed: {
    ...mapState("projectsStore", ["projects"]),
    ...mapState("projectStore", [
      "name",
      "standard",
      "available_standards",
      "equipment_type",
      "dateCalibrated",
      "equipments",
      "buildings",
    ]),
    ...mapState("authStore", [
      "activeSubscription",
      "email",
      "firstname",
      "lastname",
    ]),
    ...mapState("companyInfoStore", [
      "companyName",
      "companyAddress",
      "companyEmail",
      "companyPhone",
    ]),
    ...mapState("loaderStore", ["cancelPdfGeneration"]),
    addProjectModalInputs() {
      return [
        {
          label: this.$i18n.t("project_list.modal.add.project"),
          value: this.name,
          mutation: "projectStore/updateProjectName",
        },
        {
          label: this.$i18n.t("project_list.modal.add.fa_standard"),
          value: this.standard,
          mutation: "projectStore/updateStandard",
          selectValues: this.available_standards,
          selectItemText: "standard_name",
          selectItemValue: "standard_id",
          rules: [
            (v) =>
              !!v || this.$i18n.t("project_list.modal.add.fa_standard_error"),
          ],
        },
      ];
    },
    companyInfoModalInputs() {
      return [
        {
          label: this.$i18n.t("project_list.modal.company_info.name"),
          value: this.companyName,
          readonly: true,
          placeholder: "e.g. Company ABC",
        },
        {
          label: this.$i18n.t("project_list.modal.company_info.address"),
          value: this.companyAddress,
          readonly: true,
          placeholder: "e.g. Stenager 5 6400 Sønderborg",
        },
        {
          label: this.$i18n.t("project_list.modal.company_info.email"),
          value: this.companyEmail,
          readonly: true,
          placeholder: "e.g. martin.manager@gmail.com",
        },
        {
          label: this.$i18n.t("project_list.modal.company_info.phone"),
          value: this.companyPhone,
          readonly: true,
          placeholder: "e.g. +456 1234567",
        },
      ];
    },
  },
  methods: {
    ...mapMutations("projectStore", [
      "setProjectId",
      "updateEquipmentType",
      "updateDateCalibrated",
      "setGeneratingPdf",
      "resetProjectState",
    ]),
    ...mapMutations("projectsStore", [
      "setSelectedProjectId",
      "setSelectedProjectEmails",
    ]),
    ...mapMutations("loaderStore", [
      "setIsLoading",
      "setFinishPdfProgress",
      "setCancelPdfGeneration",
    ]),
    ...mapMutations("buildingStore", ["resetBuildingState"]),
    ...mapMutations("floorStore", ["resetFloorState"]),
    ...mapActions("projectStore", [
      "addNewProject",
      "addEquipment",
      "getProjectById",
      "setProjectState",
      "generatePdf",
    ]),
    openProject(project) {
      this.setProjectId(project.project_id);
      this.$router.push(`project/${project.project_id}`);
    },
    getDropdownItems(state) {
      const items = [
        this.$i18n.t("project_list.dropdown.share"),
        {
          value: this.$i18n.t("project_list.dropdown.delete_project"),
          warning: true,
        },
        this.$i18n.t("project_list.dropdown.generate_report"),
      ];
      if (state !== "in_configuration") {
        items.unshift(this.$i18n.t(`project_list.dropdown.rollback.${state}`));
      }
      return items;
    },
    addNew() {
      this.setIsLoading(true);
      this.addNewProject().then(() => {
        this.setIsLoading(false);
      });
    },
    projectState(state) {
      return getProjectStatePresentationName(state);
    },
    deleteProjectClicked(project) {
      this.selectedProject = project;
      this.isDeletingModalVisible = true;
    },
    async executeSelectedAction(data) {
      if (
        data.value === this.$i18n.t("project_list.dropdown.delete_project") &&
        (this.activeSubscription || data.project.users.length === 0)
      ) {
        this.deleteProjectClicked(data.project);
      } else if (
        data.value === this.$i18n.t("project_list.dropdown.share") &&
        this.activeSubscription
      ) {
        this.isShareModalVisible = true;
        this.selectedProject = data.project;
        this.setSelectedProjectId(data.project.project_id);
        const selectedProjectEmails = data.project.users.map(
          (user) => user.email
        );
        this.setSelectedProjectEmails(selectedProjectEmails || []);
      } else if (
        data.value === this.$i18n.t("project_list.dropdown.share") ||
        data.value === this.$i18n.t("project_list.dropdown.delete_project")
      ) {
        this.isGeneralUpgradeModalVisible = true;
      } else if (
        data.value === this.$i18n.t("project_list.dropdown.generate_report")
      ) {
        this.setIsLoading(true);
        this.setGeneratingPdf(true);
        await this.getProjectById(data.project.project_id);
        let noOfRooms = 0;
        this.buildings.forEach((building) => {
          building.floors.forEach((floor) => {
            if (floor.rooms) {
              noOfRooms = noOfRooms + floor.rooms.length;
            }
          });
        });
        if (noOfRooms === 0) {
          this.resetProjectState();
          this.resetBuildingState();
          this.resetFloorState();
          this.setCancelPdfGeneration(false);
          this.setIsLoading(false);
          return;
        }
        if (this.cancelPdfGeneration) {
          this.resetProjectState();
          this.resetBuildingState();
          this.resetFloorState();
          this.setCancelPdfGeneration(false);
          return;
        }
        await this.generatePdf();
        if (!this.cancelPdfGeneration) {
          this.setFinishPdfProgress(true);
          setTimeout(() => {
            this.setIsLoading(false);
            this.setGeneratingPdf(false);
            this.resetProjectState();
            this.resetBuildingState();
            this.resetFloorState();
            this.setFinishPdfProgress(false);
          }, 1000);
        } else {
          this.setCancelPdfGeneration(false);
          this.resetProjectState();
          this.resetBuildingState();
          this.resetFloorState();
        }
      } else if (!this.activeSubscription && data.project.users.length > 0) {
        this.isGeneralUpgradeModalVisible = true;
      } else {
        this.isRollbackModalVisible = true;
        this.selectedProject = data.project;
      }
    },
    onDelete() {
      this.setProjectState({
        project_id: this.selectedProject.project_id,
        newState: "deleted",
      });
      this.isDeletingModalVisible = false;
    },
    async onRollback() {
      let newState;
      switch (this.selectedProject.state) {
        case "in_testing":
          newState = "in_configuration";
          break;
        case "in_evaluation":
          newState = "in_testing";
          break;
        case "completed":
          newState = "in_evaluation";
      }
      this.isRollbackModalVisible = false;
      await this.setProjectState({
        project_id: this.selectedProject.project_id,
        newState,
      });
      this.$router.push(`project/${this.selectedProject.project_id}`);
    },
    openFastSpringPopup() {
      this.isGeneralUpgradeModalVisible = false;
      window.fastspring.builder.recognize({
        email: this.email,
        firstName: this.firstname,
        lastName: this.lastname,
      });
      window.fastspring.builder.add("fa-assistent-premium");
      window.fastspring.builder.checkout();
    },
  },
};
</script>

<style lang="scss">
.v-text-field--full-width .v-input__append-inner,
.v-text-field--enclosed .v-input__append-inner {
  margin-top: 7px !important;
}

/* By making the list black, the scroll bar disappeared in dark mode */
.project-list {
  ::-webkit-scrollbar {
    background-color: #202324;
    color: #aba499;
    width: 8px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: #454a4d;
  }
  ::-webkit-scrollbar-thumb:hover {
    background-color: #575e62;
  }
  ::-webkit-scrollbar-thumb:active {
    background-color: #484e51;
  }
  ::-webkit-scrollbar-corner {
    background-color: #181a1b;
  }
}

.search-field {
  border-color: khaki;
}
.project-list-item {
  min-height: 80px !important;
}
</style>
